import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { db, type PracticeEntry } from "@/lib/db";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";

// ---------------------------------------------------------------------------
// Time Slot Configuration
// ---------------------------------------------------------------------------

export interface TimeSlotConfig {
  slot: 1 | 2 | 3 | 4 | 5 | 6;
  label: string;
  labelShort: string;
  emoji: string;
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  active: boolean;   // slot appears in journal
  reminder: boolean; // push reminder at start time
}

export const TIME_SLOTS: TimeSlotConfig[] = [
  { slot: 1, label: "Früh (05:00–08:00)", labelShort: "Früh", emoji: "🌅", startHour: 5, startMinute: 0, endHour: 8, endMinute: 0, active: true, reminder: true },
  { slot: 2, label: "Morgen (08:00–11:00)", labelShort: "Morgen", emoji: "☀️", startHour: 8, startMinute: 0, endHour: 11, endMinute: 0, active: true, reminder: true },
  { slot: 3, label: "Mittag (11:00–14:00)", labelShort: "Mittag", emoji: "🌤️", startHour: 11, startMinute: 0, endHour: 14, endMinute: 0, active: true, reminder: true },
  { slot: 4, label: "Nachmittag (14:00–17:00)", labelShort: "Nachmittag", emoji: "🌇", startHour: 14, startMinute: 0, endHour: 17, endMinute: 0, active: true, reminder: true },
  { slot: 5, label: "Abend (17:00–20:00)", labelShort: "Abend", emoji: "🌆", startHour: 17, startMinute: 0, endHour: 20, endMinute: 0, active: true, reminder: true },
  { slot: 6, label: "Nacht (20:00–00:00)", labelShort: "Nacht", emoji: "🌙", startHour: 20, startMinute: 0, endHour: 24, endMinute: 0, active: true, reminder: true },
];

// ---------------------------------------------------------------------------
// Timeslot config persistence (localStorage)
// ---------------------------------------------------------------------------

const TIMESLOT_CONFIG_KEY = "vajra-timeslot-config";

function buildSlotLabel(ts: Pick<TimeSlotConfig, "labelShort" | "startHour" | "startMinute" | "endHour" | "endMinute">): string {
  const fmt = (h: number, m: number) => `${String(h % 24).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  return `${ts.labelShort} (${fmt(ts.startHour, ts.startMinute)}–${fmt(ts.endHour, ts.endMinute)})`;
}

/** Compute end times: each active slot ends when the next active slot begins. */
function computeEndTimes(slots: TimeSlotConfig[]): TimeSlotConfig[] {
  const active = slots
    .filter((s) => s.active)
    .sort((a, b) => (a.startHour * 60 + a.startMinute) - (b.startHour * 60 + b.startMinute));
  if (active.length === 0) return slots;
  return slots.map((slot) => {
    if (!slot.active) return slot;
    const idx = active.findIndex((s) => s.slot === slot.slot);
    const next = active[(idx + 1) % active.length];
    const ts: TimeSlotConfig = { ...slot, endHour: next.startHour, endMinute: next.startMinute };
    ts.label = buildSlotLabel(ts);
    return ts;
  });
}

/** Returns HH:MM strings for all reminder-enabled slots, sorted. */
export function getReminderTimes(config: TimeSlotConfig[]): string[] {
  return config
    .filter((s) => s.reminder)
    .map((s) => `${String(s.startHour).padStart(2, "0")}:${String(s.startMinute).padStart(2, "0")}`)
    .sort();
}

export function loadTimeslotConfig(): TimeSlotConfig[] {
  try {
    const raw = localStorage.getItem(TIMESLOT_CONFIG_KEY);
    if (!raw) return TIME_SLOTS;
    const saved = JSON.parse(raw) as Array<{
      slot: number;
      startHour: number;
      startMinute: number;
      active?: boolean;
      reminder?: boolean;
    }>;
    const slots = TIME_SLOTS.map((def) => {
      const o = saved.find((s) => s.slot === def.slot);
      if (!o) return def;
      return {
        ...def,
        startHour: o.startHour,
        startMinute: o.startMinute,
        active: o.active ?? true,
        reminder: o.reminder ?? true,
      } as TimeSlotConfig;
    });
    return computeEndTimes(slots);
  } catch {
    return TIME_SLOTS;
  }
}

export function saveTimeslotConfig(config: TimeSlotConfig[]): void {
  localStorage.setItem(TIMESLOT_CONFIG_KEY, JSON.stringify(
    config.map(({ slot, startHour, startMinute, active, reminder }) => ({ slot, startHour, startMinute, active, reminder }))
  ));
}

export function resetTimeslotConfig(): void {
  localStorage.removeItem(TIMESLOT_CONFIG_KEY);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns YYYY-MM-DD in the user's local timezone (not UTC). */
function getTodayString(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/** Returns YYYY-MM-DD for an arbitrary Date in local timezone. */
function toLocalDateString(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function getCurrentTimeSlot(slots: TimeSlotConfig[] = TIME_SLOTS): TimeSlotConfig {
  const activeSlots = slots.filter((s) => s.active);
  const now = new Date();
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  for (const ts of activeSlots) {
    const start = ts.startHour * 60 + ts.startMinute;
    let end = ts.endHour * 60 + ts.endMinute;
    const wraps = end <= start;
    const t = wraps && nowMinutes < start ? nowMinutes + 1440 : nowMinutes;
    if (wraps) end += 1440;
    if (t >= start && t < end) return ts;
  }
  return activeSlots[0] ?? slots[0];
}

export function getTimeSlotStatus(
  slot: TimeSlotConfig,
  completedSlots: Set<number>,
  nowMinutes: number
): "completed" | "current" | "upcoming" | "missed" {
  const start = slot.startHour * 60 + slot.startMinute;
  let end = slot.endHour * 60 + slot.endMinute;
  // Handle midnight wrap (last active slot ends at first active slot's start next day)
  const wraps = end <= start;
  const t = wraps && nowMinutes < start ? nowMinutes + 1440 : nowMinutes;
  if (wraps) end += 1440;
  if (t >= start && t < end) return "current";
  if (t >= end) return completedSlots.has(slot.slot) ? "completed" : "missed";
  return "upcoming";
}

// ---------------------------------------------------------------------------
// Supabase <-> Local conversion
// ---------------------------------------------------------------------------

// snake_case type matching the Supabase table
interface SbRow {
  id: string;
  user_id: string;
  date: string;
  time_slot: number;
  type: string;
  text: string;
  virtue_ids: number[] | null;
  widmung: string;
  versprechen: string;
  versprechen_bis_datum: string | null;
  versprechen_erfuellt: boolean;
  promise: string;
  promise_bis_datum: string | null;
  promise_erfuellt: boolean;
  completed_at: string;
}

function toSbRow(e: PracticeEntry): SbRow {
  return {
    id: e.id,
    user_id: e.userId,
    date: e.date,
    time_slot: e.timeSlot,
    type: e.type,
    text: e.text,
    virtue_ids: e.virtueIds && e.virtueIds.length > 0 ? e.virtueIds : null,
    widmung: e.widmung,
    versprechen: e.versprechen,
    versprechen_bis_datum: e.versprechenBisDatum ?? null,
    versprechen_erfuellt: e.versprechenErfuellt ?? false,
    promise: e.promise ?? "",
    promise_bis_datum: e.promiseBisDatum ?? null,
    promise_erfuellt: e.promiseErfuellt ?? false,
    completed_at: e.completedAt,
  };
}

function fromSbRow(r: SbRow): PracticeEntry {
  return {
    id: r.id,
    userId: r.user_id,
    date: r.date,
    timeSlot: r.time_slot as 1 | 2 | 3 | 4 | 5 | 6,
    type: r.type as "positive" | "negative" | "meditation",
    text: r.text,
    virtueIds: r.virtue_ids ?? undefined,
    widmung: r.widmung,
    versprechen: r.versprechen,
    versprechenBisDatum: r.versprechen_bis_datum ?? undefined,
    versprechenErfuellt: r.versprechen_erfuellt,
    promise: r.promise || undefined,
    promiseBisDatum: r.promise_bis_datum ?? undefined,
    promiseErfuellt: r.promise_erfuellt,
    completedAt: r.completed_at,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Full sync from Supabase (paginated) — runs once per browser session per user
// ---------------------------------------------------------------------------

async function pullAllFromSupabase(userId: string): Promise<void> {
  const SESSION_KEY = `vajra-full-sync-${userId}`;
  if (sessionStorage.getItem(SESSION_KEY)) return; // already done this session

  const PAGE_SIZE = 1000;
  let offset = 0;
  const allRows: SbRow[] = [];

  try {
    while (true) {
      const { data, error } = await supabase
        .from("practice_entries")
        .select("*")
        .eq("user_id", userId)
        .range(offset, offset + PAGE_SIZE - 1)
        .order("completed_at", { ascending: true });

      if (error || !data || data.length === 0) break;
      allRows.push(...(data as SbRow[]));
      if (data.length < PAGE_SIZE) break;
      offset += PAGE_SIZE;
    }

    if (allRows.length > 0) {
      await db.practiceEntries.bulkPut(allRows.map(fromSbRow));
    }
    sessionStorage.setItem(SESSION_KEY, "1");
  } catch {
    // Offline — skip, will retry next session
  }
}

export function usePractice() {
  const { user } = useAuth();
  const [todayEntries, setTodayEntries] = useState<PracticeEntry[]>([]);
  const [streak, setStreak] = useState(0);
  const [totalEntryCount, setTotalEntryCount] = useState(0);
  const [totalSlotCount, setTotalSlotCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const userId = user?.id ?? "";
  const today = getTodayString();

  // Load today's entries: local IndexedDB first, then merge from Supabase
  const loadTodayEntries = useCallback(async () => {
    if (!userId) return;
    try {
      // 1. Local read (immediate)
      const local = await db.practiceEntries
        .where("[userId+date]")
        .equals([userId, today])
        .toArray();

      // 2. Pull from Supabase (cloud sync — covers PWA/browser isolation)
      try {
        const { data: remote } = await supabase
          .from("practice_entries")
          .select("*")
          .eq("user_id", userId)
          .eq("date", today);

        if (remote && remote.length > 0) {
          const localIds = new Set(local.map((r) => r.id));
          const newFromCloud = (remote as SbRow[])
            .filter((r) => !localIds.has(r.id))
            .map(fromSbRow);

          if (newFromCloud.length > 0) {
            await db.practiceEntries.bulkPut(newFromCloud);
            setTodayEntries([...local, ...newFromCloud]);
            return;
          }
        }
      } catch {
        // Offline or Supabase unavailable — use local only
      }

      setTodayEntries(local);
    } catch (err) {
      console.error("Failed to load today's practice entries:", err);
    }
  }, [userId, today]);

  // Calculate streak (based on local DB for speed)
  const loadStreak = useCallback(async () => {
    if (!userId) return;
    try {
      let currentStreak = 0;
      const date = new Date();

      const todayRecords = await db.practiceEntries
        .where("[userId+date]")
        .equals([userId, getTodayString()])
        .count();

      if (todayRecords > 0) {
        currentStreak = 1;
      }

      for (let i = 1; i <= 365; i++) {
        const checkDate = new Date(date);
        checkDate.setDate(checkDate.getDate() - i);
        const dateStr = toLocalDateString(checkDate);

        const count = await db.practiceEntries
          .where("[userId+date]")
          .equals([userId, dateStr])
          .count();

        if (count > 0) {
          currentStreak++;
        } else {
          break;
        }
      }

      setStreak(currentStreak);
    } catch (err) {
      console.error("Failed to calculate streak:", err);
    }
  }, [userId]);

  // Load total entry count across all days (for rotation calculation)
  const loadTotalEntryCount = useCallback(async () => {
    if (!userId) return;
    try {
      const all = await db.practiceEntries
        .where("userId")
        .equals(userId)
        .toArray();
      setTotalEntryCount(all.length);
      // Count distinct [date+timeSlot] combinations for slot-based rotation
      const distinctSlots = new Set(all.map((e) => `${e.date}-${e.timeSlot}`));
      setTotalSlotCount(distinctSlots.size);
    } catch {
      // ignore
    }
  }, [userId]);

  // Add a new practice entry (write to local + sync to Supabase)
  const addEntry = useCallback(
    async (entry: Omit<PracticeEntry, "id" | "completedAt" | "userId">) => {
      if (!userId) throw new Error("User not authenticated");

      const record: PracticeEntry = {
        ...entry,
        userId,
        id: crypto.randomUUID(),
        completedAt: new Date().toISOString(),
      };

      // Local write (immediate)
      await db.practiceEntries.add(record);

      // Cloud sync (best-effort — silent on failure when offline)
      supabase
        .from("practice_entries")
        .upsert(toSbRow(record))
        .then(() => {}, () => {});

      await loadTodayEntries();
      await loadStreak();
      await loadTotalEntryCount();
    },
    [userId, loadTodayEntries, loadStreak, loadTotalEntryCount]
  );

  // Delete an entry
  const deleteEntry = useCallback(
    async (id: string) => {
      await db.practiceEntries.delete(id);
      supabase.from("practice_entries").delete().eq("id", id).then(() => {}, () => {});
      await loadTodayEntries();
      await loadStreak();
    },
    [loadTodayEntries, loadStreak]
  );

  // Mark Ausgleichshandlung as done
  const fulfillPromise = useCallback(
    async (id: string) => {
      await db.practiceEntries.update(id, { versprechenErfuellt: true });
      supabase
        .from("practice_entries")
        .update({ versprechen_erfuellt: true })
        .eq("id", id)
        .then(() => {}, () => {});
      await loadTodayEntries();
    },
    [loadTodayEntries]
  );

  // Mark Versprechen as kept
  const fulfillVersprechen = useCallback(
    async (id: string) => {
      await db.practiceEntries.update(id, { promiseErfuellt: true });
      supabase
        .from("practice_entries")
        .update({ promise_erfuellt: true })
        .eq("id", id)
        .then(() => {}, () => {});
      await loadTodayEntries();
    },
    [loadTodayEntries]
  );

  // Derived: entries grouped by slot
  const entriesPerSlot = useMemo(() => {
    const grouped: Record<number, PracticeEntry[]> = {};
    for (const entry of todayEntries) {
      if (!grouped[entry.timeSlot]) {
        grouped[entry.timeSlot] = [];
      }
      grouped[entry.timeSlot].push(entry);
    }
    return grouped;
  }, [todayEntries]);

  // Derived: completed slots (slots with at least 1 entry)
  const completedSlots = useMemo(
    () => new Set(todayEntries.map((e) => e.timeSlot)),
    [todayEntries]
  );

  // Dynamic timeslot config (reads localStorage once on mount)
  const [timeSlots] = useState(loadTimeslotConfig);

  // Reactive current time slot + nowMinutes — re-check every 60 s
  const getNowMinutes = () => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  };
  const [currentTimeSlot, setCurrentTimeSlot] = useState(() =>
    getCurrentTimeSlot(timeSlots)
  );
  const [nowMinutes, setNowMinutes] = useState(getNowMinutes);
  const slotIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    slotIntervalRef.current = setInterval(() => {
      setCurrentTimeSlot(getCurrentTimeSlot(timeSlots));
      setNowMinutes(getNowMinutes());
    }, 60_000);
    return () => {
      if (slotIntervalRef.current) clearInterval(slotIntervalRef.current);
    };
  }, [timeSlots]);

  // Derived: entries with open commitments (unfulfilled Ausgleichshandlung or Versprechen)
  const openPromises = useMemo(
    () =>
      todayEntries.filter(
        (e) =>
          e.type === "negative" &&
          ((e.versprechen && e.versprechenErfuellt !== true) ||
            (e.promise && e.promiseErfuellt !== true))
      ),
    [todayEntries]
  );

  // Initial load — full Supabase sync first, then local reads
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    pullAllFromSupabase(userId).finally(() => {
      Promise.all([loadTodayEntries(), loadStreak(), loadTotalEntryCount()]).finally(() =>
        setLoading(false)
      );
    });
  }, [userId, loadTodayEntries, loadStreak, loadTotalEntryCount]);

  return {
    // State
    todayEntries,
    loading,

    // Computed
    timeSlots,
    currentTimeSlot,
    nowMinutes,
    entriesPerSlot,
    completedSlots,
    streak,
    openPromises,
    totalEntryCount,
    totalSlotCount,

    // Actions
    addEntry,
    deleteEntry,
    fulfillPromise,
    fulfillVersprechen,
    reload: loadTodayEntries,
  };
}
